import { once, throttle } from 'lodash-es'
import dynamic from 'next/dynamic'
import { ComponentType } from 'react'
import { loadPMUserListHistory } from '../actions/message'
import { workerArchive } from '../actions/workerActions'
import type { NewsSearchType } from '../libraries/mfc/MfcJsTypes'
import { getGalleryData } from '../mfcLogic/api/privateGallery'
import { safeDecodeUriComponent } from '../utilities/safeDecodeUriComponent'
import { TransitionType, TransitionTypes } from '../utilities/transitions'
import { pubSubRouter } from './pubSub'
import { checkMail } from './state.mail'
import { getNews, search } from './state.news'
import { slugToID } from './state.users'

export type RouteHandlerOptions = {
    initial?: boolean
    closeRoom?: boolean
    replaceState?: boolean
    coords?: { x: number; y: number }
    baseRoute?: string
    skipPreload?: boolean
    transitionSource?: TransitionType
}

export type RouteInfo = {
    currentRoute: string
    pendingRoute: string
    currentQuery?: { [key: string]: string }
    pendingQuery?: { [key: string]: string }
    currentSlug?: string
    pendingSlug?: string
    currentSurrogateSlug?: string
    pendingSurrogateSlug?: string
    fullRoute: string
}

export type RouteHandler = (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => PromiseLike<any>

export type RouterEventCallback = (
    routeInfo: RouteInfo,
    initial: boolean,
) => void

export interface MfcRouter {
    route: {
        visibleRoute: string
        visiblePage: string
        floatingPage: string
        floatingRoute: string
        floatingComponent: ComponentType<{
            ssr?: boolean
            fullWindow?: boolean
            isMobile?: boolean
            vertical?: boolean
        }>
        basePage: string
        baseRoute: string
        baseComponent: ComponentType<{
            ssr?: boolean
            fullWindow?: boolean
            isMobile?: boolean
            vertical?: boolean
        }>
        slug: string
        slugID?: number
        surrogateSlug: string
        surrogateID: number
        visibleQuery: { [key: string]: string }
        baseQuery: { [key: string]: string }
        hash: string
        preloadBaseData: any
        preloadFloatingData: any
        // preloadBaseData: any // shouldn't really be necessary
        lastTransitionSource: TransitionType
    }
    init: (initialPath: string) => void
    events: {
        emit: (
            event: 'ready' | 'completed' | 'change',
            data?: any,
            immediate?: boolean,
        ) => void
        onReady: (callback: (initialPath: string) => void) => () => void
        onChange: (callback: RouterEventCallback) => () => void
        onCompleted: (callback: RouterEventCallback) => () => void
        offCompleted: (callback: RouterEventCallback) => void
        _ready: Array<(initialPath: string) => void>
        _change: Array<RouterEventCallback>
        _completed: Array<RouterEventCallback>
    }
}

export interface RouteConfig {
    /** the imported component (dynamic or otherwise) */
    component: ComponentType<{
        ssr?: boolean
        fullWindow?: boolean
        isMobile?: boolean
        vertical?: boolean
    }>
    /** the routes that can match to this component */
    paths: Array<string>
    /** whether or not this is a floating route */
    floater?: boolean
    /** if its the default initial route */
    default?: boolean
    /** skip preload during routing */
    skipRouterPreload?: boolean
    /** don't await the preload */
    skipAwait?: boolean
    /** function that can be run to preload data if opportunity arises (and/or just during normal routing)
     *   - should be debounced or idempotent
     */
    preload?: () => Promise<any>
}

export type RouteHistoryItem = { visibleRoute: string; baseRoute: string }
export type RouteHistory = {
    lastRoute: [lastRoute: RouteHistoryItem, currentRoute: RouteHistoryItem]
    referrer: string
}
export const HISTORY: RouteHistory = {
    lastRoute: [
        { visibleRoute: '', baseRoute: '' },
        { visibleRoute: '', baseRoute: '' },
    ],
    referrer: null,
}

// const archivesPromise = import('./state.archives')
// const mailPromise = import('./state.mail')
// const newsPromise = import('./state.news')
const roomPromise = import(
    '../components/presentation/Modals/FullscreenFloater'
)
const FullscreenFloater = dynamic(() => roomPromise)

export const routeConfigs = {
    get: (path: string, floater?: boolean) => {
        // console.log('[routeConfigs.get()]', { path })
        const route = routeConfigs.routes.find(
            (route) =>
                route.paths.includes(path) &&
                (floater !== undefined ? route.floater === floater : true),
        )
        if (!route) {
            // check if path can be matched to a slug
            const isSlug = !!slugToID[path]
            if (isSlug)
                return routeConfigs.routes.find(
                    (route) => route.paths.includes('[slug]'), // this is really naive but we don't do anything more than this
                )
            return routeConfigs.routes.find((route) =>
                route.paths.includes('error'),
            )
        }
        return route
    },
    routes: [
        {
            component: dynamic(() => import('../pages/404')),
            paths: ['error'], // when is this used vs _error?
        },
        {
            component: dynamic(() => import('../pages/chat-archives')),
            paths: ['chat-archives'],
            preload: once(() => workerArchive(null)),
            // skip this during normal router loads as `handleChatArchivesRouting` calls it after awaiting modelslist
            // - if we do it here the socket is connected already so it should be safe
            skipRouterPreload: true,
        },
        {
            component: dynamic(() => import('../pages/friends')),
            paths: ['friends'],
        },
        {
            component: dynamic(() => import('../pages/index')),
            paths: ['', 'home'],
            default: true,
        },
        {
            component: dynamic(() => import('../pages/mail')),
            paths: ['mail'],
            preload: throttle(checkMail, 60000),
        },
        {
            component: dynamic(() => import('../pages/messages')),
            paths: ['messages'],
            preload: once(loadPMUserListHistory),
            skipRouterPreload: true,
        },
        {
            component: dynamic(() => import('../pages/news')),
            paths: ['news'],
            preload: throttle(() => {
                if (AppRouter.route.visiblePage === 'news') {
                    const { query, type } = AppRouter.route.baseQuery
                    if (query) {
                        search((type as NewsSearchType) || 'news', query)
                        return
                    }
                }
                getNews({
                    update_only: 1,
                })
            }, 45000),
        },
        {
            component: dynamic(() => import('../pages/notes')),
            paths: ['notes'],
        },
        {
            component: dynamic(() => import('../pages/private-gallery')),
            paths: ['private-gallery'],
            preload: once(getGalleryData),
            skipAwait: true,
        },
        {
            component: dynamic(() => import('../pages/recommendations')),
            paths: ['recommendations'],
        },
        {
            component: dynamic(() => import('../pages/rooms')),
            paths: ['rooms'],
        },
        {
            component: dynamic(() => import('../pages/search')),
            paths: ['search', 'explore'],
        },
        {
            component: FullscreenFloater,
            paths: ['room'],
            floater: true,
        },
        {
            component: FullscreenFloater,
            paths: ['message'],
            floater: true,
        },
        {
            component: FullscreenFloater,
            paths: ['profile', '[slug]'], // how do we match to this?
            floater: true,
        },
    ] as Array<RouteConfig>,
}

export const getRouteSegments = (
    fullRoute: string = '',
): [
    page: string,
    slug: string,
    surrogateSlug: string,
    query: { [key: string]: string },
    hash: string,
    fullRoute: string,
] => {
    const lowerFullRoute = fullRoute.toLowerCase()
    const [lowerDehashedFullRoute, hash] = lowerFullRoute.split('#')
    const [baseRoute, query] = lowerDehashedFullRoute.split('?')
    const [, route, slug = '', surrogateSlug = ''] = baseRoute.split('/')
    const compiledQuery = {}
    if (query) {
        const groups = query.split('&')
        groups.forEach((group) => {
            const [key, value] = group.split('=')
            compiledQuery[safeDecodeUriComponent(key)] = safeDecodeUriComponent(
                value || '',
            )
        })
    }
    // console.log('[getRouteSegment]', lowerFullRoute, {
    //     baseRoute,
    //     compiledQuery,
    // })

    return [route, slug, surrogateSlug, compiledQuery, hash, lowerFullRoute]
}

const setupCallbacks = (
    type: '_ready' | '_change' | '_completed',
    callback,
) => {
    AppRouter.events[type].push(callback)
    return () => {
        const index = AppRouter.events[type].findIndex((cb) => cb === callback)
        if (index >= 0) AppRouter.events[type].splice(index, 1)
    }
}

export const AppRouter: MfcRouter = {
    route: {
        visibleRoute: undefined,
        visiblePage: undefined,
        floatingPage: undefined,
        floatingRoute: undefined,
        floatingComponent: undefined,
        basePage: undefined,
        baseRoute: undefined,
        baseComponent: undefined,
        visibleQuery: {},
        baseQuery: {},
        hash: undefined,
        slug: undefined,
        slugID: undefined,
        surrogateSlug: undefined,
        surrogateID: undefined,
        preloadBaseData: undefined,
        preloadFloatingData: undefined,
        lastTransitionSource: TransitionTypes.NONE,
    },
    init: (initialPath) => {
        const [page, slug, surrogateSlug, query, hash] =
            getRouteSegments(initialPath)
        const route = routeConfigs.routes.find((route) =>
            route.paths.includes(page),
        )
        const defaultRoute = routeConfigs.routes.find((route) => route.default)
        const foundRoute = route || defaultRoute
        if (!foundRoute.floater) {
            AppRouter.route.baseComponent = foundRoute.component
            AppRouter.route.basePage = foundRoute.paths[0]
            AppRouter.route.baseRoute = '/' + foundRoute.paths[0]
        } else {
            // ensure default route gets set as the base if we direct load a room/pm/profile
            AppRouter.route.baseComponent = defaultRoute.component
            AppRouter.route.basePage = defaultRoute.paths[0]
            AppRouter.route.baseRoute = '/' + defaultRoute.paths[0]
        }
        AppRouter.route.baseQuery = query
        AppRouter.route.visibleQuery = query
        AppRouter.route.hash = hash
        AppRouter.route.slug = slug
        AppRouter.route.surrogateSlug = surrogateSlug
        AppRouter.events.emit('ready', initialPath, false)
    },
    events: {
        emit: (event, data, immediate = true) =>
            pubSubRouter.emit(event, data, immediate),
        onReady: (callback) => setupCallbacks('_ready', callback),
        onChange: (callback) => setupCallbacks('_change', callback),
        onCompleted: (callback) => setupCallbacks('_completed', callback),
        offCompleted: (callback) => {
            const index = AppRouter.events._completed.findIndex(
                (cb) => cb === callback,
            )
            if (index >= 0) AppRouter.events._completed.splice(index, 1)
        },
        _ready: [],
        _change: [],
        _completed: [],
    },
}

pubSubRouter.on('ready', (initialPath) => {
    AppRouter.events._ready.forEach((callback) => callback(initialPath))
})
pubSubRouter.on('change', ({ routeInfo, initial }) => {
    AppRouter.events._change.forEach((callback) => callback(routeInfo, initial))
})
pubSubRouter.on('completed', ({ routeInfo, initial }) => {
    AppRouter.events._completed.forEach((callback) =>
        callback(routeInfo, initial),
    )
})

if (typeof window !== 'undefined') {
    window.__ROUTER__ = { AppRouter, HISTORY }
}
